import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'
import { addFavContracts, delFavContracts, getFavContracts, getHotContracts, getRiskLimitsAll, getSwapOpen, getSwapSetting, getWebInstruments, setSwapSetting } from '~/apis/swap'
import { useSwapClientStore } from './swapClient'
import { useSwapMarketStore } from './swapMarket'
import { useStore } from '.'

export const useSwapStore = defineStore('swap', {
  state: () => ({
    symbol: '',
    tradeType: 'linearPerpetual', // 交易类型
    isOpen: undefined, // 是否开启合约
    defaultSymbol: 'BTCUSDT', // 默认合约代码
    contracts: [], // 合约列表
    contractsRaw: {}, // 合约列表原始数据
    favContracts: [], // 自选合约列表
    hotContracts: [], // 热门合约列表
    bbo: {},
    unit: 'base',
    step: '',
    isHedgeMode: true,
    riskLimitsRaw: {},
    orderConfirm: {}, // keys 1:市价 2:限价 3:条件单 4: 一键全平 5: 一键撤单， 用于二次确认
    modal: {
      transfer: false,
      picker: false,
      adjustLeverage: false,
      calculator: false,
      unitSetting: false,
      flashAll: false,
      reverse: false,
      cancelAll: false,
      orderConfirm: false,
    },
    modalData: {
      adjustLeverage: {},
      reverse: {},
    },
  }),
  getters: {
    visibleContracts: (s) => {
      return s.contracts.filter(i => i.isShow)
    },
    base() {
      return this.curInfo.base
    },
    quote() {
      return this.curInfo.quote
    },
    baseM() {
      return this.curInfo.baseM || 'USDT'
    },
    scale() {
      return this.curInfo.scale ?? 3
    },
    size() {
      return this.curInfo.size ?? 1
    },
    ps() {
      return this.curInfo.ps ?? 3
    },
    as() {
      return this.curInfo.as ?? 3
    },
    bmsp() {
      return this.getSp(this.baseM) || 2
    },
    getSp() {
      const store = useStore()
      // 产品非要写死，有啥办法呢
      return code => code === 'USDT' ? 8 : store.getSp(code) || 2
    },
    curInfo(s) {
      return s.contractsRaw[s.symbol] || {}
    },
    getInfo: s => (symbol = s.symbol) => {
      return s.contractsRaw[symbol] || {}
    },
    steps() {
      const { frontPrecisions } = this.curInfo
      return frontPrecisions?.split(',')
    },
    isLot() {
      return this.unit === 'lot'
    },
    isLotBase() {
      return this.unit === 'base'
    },
    isLotQuote() {
      return this.unit === 'quote'
    },
    isLotCost() {
      return this.unit === 'cost'
    },
    getRealAmount() {
      const swapMarket = useSwapMarketStore()
      return (n, { forceQuote = false, symbol = this.symbol, price } = {}) => {
        const { mark } = swapMarket.getOverview(symbol)
        console.log(99, mark)

        const { size = 1 } = this.getInfo(symbol)
        price ||= mark
        if (forceQuote || this.isLotCost) {
          return mul(n, price).mul(size)
        }
        else if (this.isLotBase) {
          return mul(n, size)
        }
        return n
      }
    },
    getLotsByCode() {
      return symbol => ({
        '-1': 0,
        '1': 0,
        'max': 0,
        'min': 0,
        'total': 0,
      })
    },
    /**
     *
     * 计算当前合约成本
     * 合约成本(U): cost = 价格 * 面值(contractSize) * 初始保证金率(1 / 杠杆倍数)  + price * 合约面值 * taker手续费 * 2
     */
    getOneLotCost() {
      const swapClient = useSwapClientStore()
      const swapMarket = useSwapMarketStore()
      return ({ price, symbol = this.symbol, leverage, pe = 1 }) => {
        price ||= swapMarket.getOverview(symbol).lp
        leverage ??= swapClient.getPositionConfig(symbol)?.leverage
        const { size = 1, takerRate = 0 } = this.getInfo(symbol)
        const olc = mul(price, size)
        const leverageFactor = div(1, leverage)
        const feeFactor = mul(takerRate, 2)
        return pe === 1 ? olc.mul(add(leverageFactor, feeFactor)) : olc.div(leverage)
      }
    },
    getRiskLimit() {
      return ({ symbol = this.symbol, marginType = 'cross', leverage, positionMargin } = {}) => {
        const riskLimits = this.riskLimitsRaw[symbol] ?? []
        // positionMargin 仓位价值，获取维持保证金率用仓位价值取
        if (+positionMargin) {
          return riskLimits.find(i => +positionMargin <= +i.notionalCap && i.marginType === marginType) || {}
        }
        else {
          return [...riskLimits].reverse().find(i => +leverage <= +i.initialLeverage && i.marginType === marginType) || {}
        }
      }
    },
  },
  actions: {
    showModal(key) {
      this.modal[key] = true
    },
    hideModal(key) {
      this.modal[key] = false
    },
    async setSwapSetting({ tradeType = this.tradeType, tradeUnit, orderType } = {}) {
      this.unit = tradeUnit || this.unit
      const params = Object.assign({ tradeType }, tradeUnit
        ? {
            tradeUnit: tradeUnit === 'base' ? 1 : 2,
          }
        : {}, orderType ? { orderType } : {})

      const res = await setSwapSetting(params)
      if (res.success) {
        this.getSwapSetting()
      }
      return res
    },
    async getSwapSetting() {
      const { success, data } = await getSwapSetting({ tradeType: this.tradeType })
      if (success) {
        this.updateSetting(data)
      }
    },
    updateSetting(data) {
      this.unit = data.tradeUnit === 1 ? 'base' : 'cost'
      const orderConfirm = Array.from({ length: 5 }).fill().map((i, index) => index + 1).reduce((acc, next) => {
        acc[next] = false
        return acc
      }, {})
      this.orderConfirm = (data.orderType || '').split(',').filter(Boolean).reduce((acc, next) => {
        acc[next] = true
        return acc
      }, orderConfirm)
    },
    async getSwapOpen() {
      const { success, data } = await getSwapOpen()
      if (success) {
        this.isOpen = data
      }
    },
    async getRiskLimitsAll({ tradeType = this.tradeType } = {}) {
      const riskLimitAllStorage = useStorage(`swap_riskLimitsAll_${tradeType}`, {})
      if (riskLimitAllStorage.value) {
        const { updateTime, storageData } = riskLimitAllStorage.value || {}
        if (Date.now() - updateTime < 30 * 60 * 1000) { // 30分钟更新一次
          this.riskLimitsRaw = storageData
          return
        }
      }
      const { success, data } = await getRiskLimitsAll({ tradeType })
      if (success) {
        this.riskLimitsRaw = data
        riskLimitAllStorage.value = {
          updateTime: Date.now(),
          storageData: data,
        }
      }
    },
    async fetchContracts() {
      const { data } = await useFetch('/api/getContracts')
      if (data?.value?.success) {
        return { success: true, data: data.value.data }
      }
      else {
        return await getWebInstruments()
      }
    },
    async getContracts(force) {
      const { success, data } = await (force || import.meta.client ? getWebInstruments() : this.fetchContracts())
      if (success) {
        this.contracts = data.map((i) => {
          const [,size] = (+i.ctVal)?.toExponential().split('e') ?? ['', 0]
          const scale = +size > 0 ? 0 : -size
          const [, _size] = (+i.tickSize)?.toExponential().split('e') ?? ['', 0]
          const priceStep = +_size > 0 ? 0 : -_size
          return {
            ...i,
            code: i.symbol,
            baseM: i.settleCurrency,
            isShow: [2, 3, 4, 5, 7].includes(+i.status),
            as: Math.max(+i.basePrecision, scale),
            ps: priceStep,
            size: i.ctVal,
            scale,
            disabledTrading: +i.status !== 3,
            maxLeverage: Math.max(...i.frontLeverages.split(',')),
          }
        })
        this.contractsRaw = this.contracts.reduce((acc, next) => {
          acc[next.code] = next
          return acc
        }, {})
      }
    },
    async getFavContracts(tradeType = this.tradeType) {
      const { success, data } = await getFavContracts({ tradeType })
      if (success) {
        this.favContracts = data
      }
    },
    async getHotContracts() {
      const { success, data } = await getHotContracts()
      if (success) {
        this.hotContracts = data?.symbolLabelList || []
      }
    },
    async toggleFav(symbol, tradeType = this.tradeType) {
      const symbolList = Array.isArray(symbol) ? symbol : [symbol]
      const favSymbols = symbolList.filter(s => this.favContracts.includes(s))
      const nonFavSymbols = symbolList.filter(s => !this.favContracts.includes(s))

      // 如果有收藏 取消收藏
      if (favSymbols.length > 0) {
        const { success: delSuccess } = await delFavContracts({ symbolList: favSymbols, tradeType })
        if (!delSuccess)
          return
      }

      // 如果没收藏 添加收藏
      if (nonFavSymbols.length > 0) {
        const { success: addSuccess } = await addFavContracts({ symbolList: nonFavSymbols, tradeType })
        if (!addSuccess)
          return
      }
      this.getFavContracts()
    },
    _localFormatter() {
      return new Intl.NumberFormat('en-us', { maximumSignificantDigits: 20 })?.format
    },
    toLocal() { // 千分符 123,456.78
      return (n) => {
        if (Number.isNaN(n) || !this._localFormatter || +n === 0)
          return n

        return this._localFormatter(n)
      }
    },
    toHuman(n, precision) { // KMB缩写 1.23M
      return Number.isNaN(+n) ? n : n < 1e6 ? fix(n, precision) : (+n).toLocaleString(this.lang, { notation: 'compact', compactDisplay: 'short', maximumFractionDigits: 3, roundingPriority: 'lessPrecision', useGrouping: false })
    },
  },
})
